import React, { useState, useEffect } from 'react'
import { Card, CardHeader, CardBody, Label, Input, Button, Badge } from '@byscott-io/corebyscott'
import { withUserStore } from '../../../contexts/UserStore'
import Lex from '../../Shared/Lex'
import { Store } from 'react-notifications-component'

const DailyJournalPanel = ({ userStore, onJournalSubmit }) => {
  const [content, setContent] = useState('')
  const [selectedGoal, setSelectedGoal] = useState('')
  const [selectedTask, setSelectedTask] = useState('')
  const [goals, setGoals] = useState([])
  const [tasks, setTasks] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [recentEntries, setRecentEntries] = useState([])

  useEffect(() => {
    // Load goals for the current account
    if (userStore.account?.goals) {
      setGoals(userStore.account.goals)
    }
  }, [userStore.account?.goals])

  useEffect(() => {
    // Load tasks for selected goal
    if (selectedGoal && userStore.account?.tasks) {
      const goalTasks = userStore.account.tasks.filter(task => task.goalId === parseInt(selectedGoal))
      setTasks(goalTasks)
      setSelectedTask('') // Reset task selection when goal changes
    } else {
      setTasks([])
      setSelectedTask('')
    }
  }, [selectedGoal, userStore.account?.tasks])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!content.trim() || !selectedGoal) return

    setIsLoading(true)
    
    try {
      userStore.createJournalEntry(
        content.trim(),
        userStore.account.id,
        parseInt(selectedGoal),
        selectedTask ? parseInt(selectedTask) : null,
        (newEntry) => {
          console.log('Journal entry created:', newEntry)
          
          Store.addNotification({
            title: 'Journal Entry Added',
            message: 'Your daily progress has been logged!',
            type: 'success',
            insert: 'bottom',
            container: 'bottom-left',
            animationIn: ['animate__animated', 'animate__fadeIn'],
            animationOut: ['animate__animated', 'animate__fadeOut'],
            dismiss: { duration: 3000 }
          })

          // Reset form
          setContent('')
          setSelectedGoal('')
          setSelectedTask('')
          
          // Add to recent entries - only if newEntry is valid
          if (newEntry && newEntry.content) {
            setRecentEntries(prev => [newEntry, ...prev.slice(0, 2)])
          }
          
          // Trigger activity feed reload
          if (onJournalSubmit) {
            onJournalSubmit()
          }
        }
      )
    } catch (error) {
      Store.addNotification({
        title: 'Error',
        message: `Failed to save journal entry: ${error.message || 'Unknown error'}`,
        type: 'danger',
        insert: 'bottom',
        container: 'bottom-left',
        dismiss: { duration: 5000 }
      })
    }
    
    setIsLoading(false)
  }

  const getGoalBadgeColor = (goal) => {
    // Use different colors based on priority or status
    const colors = ['primary', 'info', 'success', 'warning']
    return colors[goal.id % colors.length]
  }

  return (
    <div className="cbs-scope" style={{ margin: '8px' }}>
      <Card>
        <CardHeader className="bg-light">
          <div className="d-flex align-items-center">
            <i className="fas fa-journal-whills text-primary me-2"></i>
            <h6 className="mb-0 fw-bold">Daily Progress Journal</h6>
          </div>
        </CardHeader>

        <CardBody>
          <form onSubmit={handleSubmit}>

            {/* Goal and Task Selection - Always Side by Side */}
            <div className="mb-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="goal-select" className="fw-semibold small mb-1">
                  <i className="fas fa-bullseye text-success me-1"></i>
                  <Lex term="goal" /> <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  id="goal-select"
                  value={selectedGoal}
                  onChange={(e) => setSelectedGoal(e.target.value)}
                  required
                >
                  <option value="">Choose a <Lex term="goal" />...</option>
                  {goals.map(goal => (
                    <option key={goal.id} value={goal.id}>
                      {goal.name}
                    </option>
                  ))}
                </Input>
              </div>

              <div>
                <Label htmlFor="task-select" className="fw-semibold small mb-1">
                  <i className="fas fa-tasks text-info me-1"></i>
                  <Lex term="task" /> (optional)
                </Label>
                <Input
                  type="select"
                  id="task-select"
                  value={selectedTask}
                  onChange={(e) => setSelectedTask(e.target.value)}
                  disabled={!selectedGoal}
                >
                  <option value="">Choose a <Lex term="task" />...</option>
                  {tasks.map(task => (
                    <option key={task.id} value={task.id}>
                      {task.name}
                    </option>
                  ))}
                </Input>
              </div>
            </div>

            {/* Journal Entry Content - Auto-expanding */}
            <div className="mb-3">
              <Label htmlFor="journal-content" className="fw-semibold small mb-1">
                <i className="fas fa-pen-fancy text-warning me-1"></i>
                What did you accomplish today?
              </Label>
              <Input
                type="textarea"
                id="journal-content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Describe your progress, challenges, or achievements..."
                style={{
                  minHeight: '38px',
                  height: 'auto',
                  resize: 'none',
                  overflow: 'hidden'
                }}
                onInput={(e) => {
                // Auto-expand textarea
                  e.target.style.height = 'auto'
                  e.target.style.height = e.target.scrollHeight + 'px'
                }}
              />
            </div>

            {/* Quick Context Display - More compact */}
            {(selectedGoal || selectedTask) && (
              <div className="mb-2 d-flex align-items-center">
                <small className="text-muted me-2">Logging for:</small>
                {selectedGoal && (
                  <Badge 
                    color={getGoalBadgeColor(goals.find(g => g.id === parseInt(selectedGoal)))} 
                    className="me-1 small"
                  >
                    <i className="fas fa-bullseye me-1"></i>
                    {goals.find(g => g.id === parseInt(selectedGoal))?.name}
                  </Badge>
                )}
                {selectedTask && (
                  <Badge color="secondary" className="small">
                    <i className="fas fa-tasks me-1"></i>
                    {tasks.find(t => t.id === parseInt(selectedTask))?.name}
                  </Badge>
                )}
              </div>
            )}

            {/* Submit Button */}
            <div>
              <Button
                type="submit"
                color="primary"
                size="sm"
                disabled={!content.trim() || !selectedGoal || isLoading}
                className="w-100"
              >
                {isLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin me-1"></i>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-plus me-1"></i>
                    Log Progress
                  </>
                )}
              </Button>
            </div>

          </form>

          {/* Recent Entries Preview */}
          {recentEntries.length > 0 && (
            <div className="mt-3 pt-3 border-top">
              <small className="text-muted fw-semibold">Recent entries:</small>
              {recentEntries.filter(entry => entry && entry.content).map((entry, index) => (
                <div key={entry.id || index} className="mt-2 p-2 bg-light rounded-md small">
                  <div className="text-truncate">{entry.content}</div>
                  <small className="text-muted">
                    {entry.createdAt ? new Date(entry.createdAt).toLocaleDateString() : 'Recently'}
                  </small>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  )
}

export default withUserStore(DailyJournalPanel)
